import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import {
  apiGet,
  apiPost,
  apiPatch,
  apiDelete,
  operationsClient,
  contactEndpoints,
} from '@/lib/api';
import type { BillTo } from '@/types/contact';

export interface UpsertBillToDto {
  label: string;
  contactId: string;
  isDefault?: boolean;
  overrideAddress?: BillTo['overrideAddress'];
  overrideEmail?: string | null;
  notes?: string | null;
}

function invalidateBillToQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  contactId: string,
) {
  void queryClient.invalidateQueries({ queryKey: contactKeys.billTo(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
}

function normalizeContactId(contactId: string) {
  return contactId.trim();
}

function requireContactId(contactId: string) {
  const normalized = normalizeContactId(contactId);
  if (!normalized) {
    throw new Error('Select a customer before adding a bill-to entry.');
  }
  return normalized;
}

export function useBillTo(contactId: string) {
  const normalizedContactId = normalizeContactId(contactId);

  return useApiQuery<BillTo[]>({
    queryKey: contactKeys.billTo(normalizedContactId),
    queryFn: async () => {
      const validContactId = requireContactId(normalizedContactId);
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        contactEndpoints.billTo(validContactId),
      );
      return ((raw as unknown as { data?: unknown }).data ?? raw) as BillTo[];
    },
    enabled: Boolean(normalizedContactId),
    showErrorToast: false,
  });
}

export function useCreateBillTo(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (dto: UpsertBillToDto) => {
      const validContactId = requireContactId(contactId);
      return apiPost(operationsClient, contactEndpoints.billTo(validContactId), dto);
    },
    onSuccess: () => {
      invalidateBillToQueries(qc, requireContactId(contactId));
      toast.success('Bill-to entry created');
    },
  });
}

export function useUpdateBillTo(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ billToId, ...dto }: Partial<UpsertBillToDto> & { billToId: string }) => {
      const validContactId = requireContactId(contactId);
      return apiPatch(
        operationsClient,
        contactEndpoints.billToEntry(validContactId, billToId),
        dto,
      );
    },
    onSuccess: () => {
      invalidateBillToQueries(qc, requireContactId(contactId));
      toast.success('Bill-to entry updated');
    },
  });
}

export function useDeleteBillTo(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (billToId: string) => {
      const validContactId = requireContactId(contactId);
      return apiDelete(operationsClient, contactEndpoints.billToEntry(validContactId, billToId));
    },
    onSuccess: () => {
      invalidateBillToQueries(qc, requireContactId(contactId));
      toast.success('Bill-to entry deleted');
    },
  });
}
